import type { FormInstance } from 'antd';
import type { FulfillmentMode } from '../types';
import type { PosTabState } from '../hooks/usePosTabs';

/**
 * Config tối thiểu mà `handleFulfillmentModeChange` cần từ `Modal.confirm` —
 * chỉ khai báo các field thực sự dùng để dễ giả lập trong test, thay vì kéo
 * theo toàn bộ `ModalFuncProps` của antd.
 */
export interface FulfillmentChangeConfirmConfig {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
  onOk: () => void;
}

export interface FulfillmentChangeDeps {
  /** Chỉ cần `setFieldValue` — không đòi hỏi toàn bộ `FormInstance`. */
  form: Pick<FormInstance, 'setFieldValue'>;
  orderItemsCount: number;
  /** Thực tế truyền `Modal.confirm`; test truyền spy để bắt `onOk`. */
  confirm: (config: FulfillmentChangeConfirmConfig) => void;
  /**
   * Tab đang thao tác, CHỐT tại thời điểm user đổi chế độ (lúc mở confirm) —
   * không phải lúc `onOk` chạy. Xem chú thích ở `handleFulfillmentModeChange`.
   */
  tabId: string;
  patchTab: (id: string, patch: Partial<Omit<PosTabState, 'id' | 'name'>>) => void;
}

/**
 * Logic thuần của `POSPage.handleFulfillmentChange`, tách ra để test độc lập
 * (không cần mount cả POSPage lẫn Modal thật).
 *
 * ĐIỂM MẤU CHỐT phải giữ khi refactor: khi đơn đang có sản phẩm, `applyMode(mode)`
 * (xoá `delivery_days` lúc chuyển về INSTANT) CHỈ được gọi bên trong `onOk` của
 * confirm dialog — tức là chỉ chạy khi user bấm "Xác nhận". Nếu ai đó kéo
 * `applyMode(mode)` ra gọi eager trước/ngoài `confirm(...)`, bấm "Huỷ" vẫn sẽ
 * xoá mất `delivery_days` của đơn đang đặt trước — một bug âm thầm mà tsc/eslint
 * không bắt được.
 *
 * ĐIỂM MẤU CHỐT THỨ HAI: `onOk` chạy MUỘN TUỲ Ý so với lúc mở confirm (user có
 * thể ngồi nhìn dialog bao lâu cũng được), và nó XOÁ SẠCH giỏ. Vì vậy nó ghi
 * qua `patchTab(tabId, ...)` với `tabId` chốt sẵn từ lúc mở dialog, không phải
 * `patchActive` ("tab nào đang active lúc reducer chạy") — cùng lớp bug
 * TCR-1096. Hôm nay ca đó chưa xảy ra được: mask + `.ant-modal-wrap` của antd
 * nằm ở z-index 1000, còn `PosTabsBar` (trong Header, z-index ≤ 100) không có
 * lớp nổi nào, nên click vào tab bị mask nuốt; hotkey đổi tab cũng đã bị gate
 * bởi cờ `confirmModalOpen`. Nhưng cả hai đều là bảo vệ GIÁN TIẾP, dễ vỡ khi
 * ai đó nâng z-index thanh tab, đổi `maskClosable`, hay thay Modal bằng
 * Popconfirm. Đây là tiền của khách — ghi theo id là bảo đảm trực tiếp.
 */
export function handleFulfillmentModeChange(
  mode: FulfillmentMode,
  deps: FulfillmentChangeDeps,
): void {
  const { form, orderItemsCount, confirm, tabId, patchTab } = deps;

  // Đơn giao ngay không có hạn giao — xoá để payload không mang delivery_days rác
  // (BE set delivery_date bất kể fulfillment_type).
  const applyMode = (m: FulfillmentMode) => {
    if (m === 'INSTANT') form.setFieldValue('delivery_days', undefined);
  };

  if (orderItemsCount > 0) {
    confirm({
      title: 'Chuyển chế độ?',
      content: 'Đổi Giao ngay / Đặt trước sẽ xóa toàn bộ sản phẩm trong đơn. Tiếp tục?',
      okText: 'Xác nhận',
      cancelText: 'Huỷ',
      onOk: () => {
        applyMode(mode);
        patchTab(tabId, {
          orderItems: [],
          paymentMethod: 'cash',
          paymentAccountId: undefined,
          nonCashAmount: undefined,
          fulfillmentMode: mode,
        });
      },
    });
  } else {
    // Nhánh đồng bộ: `tabId` vẫn là tab đang active ngay lúc này, dùng chung
    // một đường ghi cho cả hai nhánh để không ai phải nhớ "nhánh nào an toàn".
    applyMode(mode);
    patchTab(tabId, { fulfillmentMode: mode });
  }
}
